import { useEffect, useRef, useState, type FormEvent } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { api, MAX_DISPUTE_IMAGE_BYTES } from "../lib/api";
import { MessageComposer } from "./MessageComposer";
import { ImageLightbox } from "./ImageLightbox";

export function DisputeMessageComposer({ value, image, busy, onChange, onImageChange, onSubmit }: {
  value: string; image: File | null; busy: boolean; onChange: (value: string) => void;
  onImageChange: (image: File | null) => void; onSubmit: (event: FormEvent) => void;
}) {
  const picker = useRef<HTMLInputElement>(null), imageButton = useRef<HTMLButtonElement>(null);
  const [preview, setPreview] = useState<string | null>(null), [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const url = URL.createObjectURL(image); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  return <MessageComposer value={value} busy={busy} maxLength={2000} onChange={onChange} onSubmit={onSubmit} attachment={{
    ready: !!image,
    action: <>
      <input ref={picker} type="file" aria-label="Choose dispute image" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={event => {
        const selected = event.target.files?.[0]; event.target.value = "";
        if (!selected) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(selected.type)) { setError("Choose a JPG, PNG or WebP image."); return; }
        if (!selected.size || selected.size > MAX_DISPUTE_IMAGE_BYTES) { setError("Choose an image up to 10 MB."); return; }
        setError(null); onImageChange(selected);
      }} />
      <button ref={imageButton} type="button" disabled={busy} aria-label="Add image" title="Add image (JPG, PNG or WebP; up to 10 MB)" onClick={() => picker.current?.click()}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-mut hover:bg-navy-50 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy disabled:opacity-40"><ImagePlus className="h-5 w-5" /></button>
    </>,
    preview: <>
      {image && <div className="mb-3 flex min-w-0 items-center gap-3 rounded-xl border border-line bg-navy-50 p-2">
        {preview && <img src={preview} alt="Image ready to send" className="h-16 w-16 shrink-0 rounded-lg object-contain" />}
        <span className="min-w-0 flex-1 truncate text-xs text-navy">{image.name}</span>
        <button type="button" disabled={busy} aria-label="Remove image" onClick={() => { onImageChange(null); setError(null); imageButton.current?.focus(); }} className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-mut hover:bg-white focus-visible:ring-2 focus-visible:ring-navy"><X className="h-5 w-5" /></button>
      </div>}
      {error && <p role="alert" className="mb-2 text-sm text-brand-red">{error}</p>}
    </>,
  }} />;
}

export function DisputeMessageImage({ disputeId, messageId, author, onAccessLost }: {
  disputeId: string; messageId: string; author: string; onAccessLost: (failure: unknown) => boolean;
}) {
  const [image, setImage] = useState<string | null>(null), [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0), [expanded, setExpanded] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null), rejectAccess = useRef(onAccessLost);
  rejectAccess.current = onAccessLost;
  useEffect(() => {
    const abort = new AbortController(); let url: string | null = null;
    setImage(null); setFailed(false);
    // [disputes.images](../../../docs/product/disputes.md#disputes-images): use the authenticated profile fetch, never a public media URL.
    // Abort late reads and revoke bytes when the case/profile loses access.
    void api.disputeMessageImage(disputeId, messageId, abort.signal).then(blob => {
      if (abort.signal.aborted) return;
      url = URL.createObjectURL(blob); setImage(url);
    }).catch((failure: unknown) => {
      if (!abort.signal.aborted && !rejectAccess.current(failure)) setFailed(true);
    });
    return () => { abort.abort(); if (url) URL.revokeObjectURL(url); };
  }, [disputeId, messageId, retry]);
  const alt = `Dispute image from ${author}`;
  return <>
    {image ? <button ref={trigger} type="button" aria-label={`View image from ${author}`} onClick={() => setExpanded(true)} className="block w-64 max-w-full overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red">
      <img src={image} alt={alt} className="h-48 w-full bg-navy-50 object-contain" />
    </button> : <div className="flex h-48 w-64 max-w-full items-center justify-center rounded-lg bg-navy-50 px-3 text-sm text-navy">
      {failed ? <div><p>Could not load this image.</p><button type="button" onClick={() => setRetry(current => current + 1)} className="btn-outline mt-2 min-h-11 text-xs">Try again</button></div>
        : <span role="status" className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading image…</span>}
    </div>}
    {expanded && image && <ImageLightbox images={[{ src: image, alt }]} initialIndex={0} onClose={() => { setExpanded(false); trigger.current?.focus(); }} />}
  </>;
}
